/**
 * Build Dynamic System Prompt Utility Module
 * Creates dynamic system prompts based on user profile data
 */

import { 
  getSystemPrompt, 
  getProfileData, 
  getSubjectPromptText,
  setDynamicSystemPrompt, 
  removeDynamicSystemPrompt 
} from '../services/sessionStorageService';
import logger from './logger';

/**
 * Build dynamic system prompt based on user profile data
 * @returns {void} No return value
 */
export const build_dynamic_system_prompt = () => {
  try {
    logger.debug('Starting dynamic system prompt build');
    
    // Clear any existing dynamic system prompt
    removeDynamicSystemPrompt();
    
    // Get base system prompt
    const systemPrompt = getSystemPrompt();
    if (!systemPrompt || !systemPrompt.prompt) {
      logger.error('Base system prompt not found in sessionStorage');
      return;
    }
    
    // Start with base prompt
    let dynamicPrompt = systemPrompt.prompt;
    
    // Get profile data
    const profileData = getProfileData();
    
    // Add firstname if exists
    if (profileData.firstname && profileData.firstname.trim()) {
      dynamicPrompt += `  The user's first name is ${profileData.firstname}.`;
    }
    
    // Add lastname if exists
    if (profileData.lastname && profileData.lastname.trim()) {
      dynamicPrompt += `  The user's last name is ${profileData.lastname}.`;
    }
    
    // Add gender if exists and not "Not Specified"
    if (profileData.gender && profileData.gender !== 'Not Specified') {
      dynamicPrompt += `  The user's gender is ${profileData.gender}.`;
    }
    
    // Add form of address if exists
    if (profileData.form_of_address && profileData.form_of_address.trim()) {
      dynamicPrompt += `\nAddress the user as ${profileData.form_of_address}`;
    }
    
    // Collect all personal facts from both personal_info and learned_information
    const allPersonalFacts = [];
    
    // Add personal facts from personal_info
    if (Array.isArray(profileData.personal_info) && profileData.personal_info.length > 0) {
      profileData.personal_info.forEach(fact => {
        if (fact && fact.fact && fact.fact.trim()) {
          allPersonalFacts.push(fact.fact);
        }
      });
    } else if (!Array.isArray(profileData.personal_info)) {
      logger.error('personal_info is not an array', { personal_info: profileData.personal_info });
    }
    
    // Add learned facts from learned_information
    if (Array.isArray(profileData.learned_information) && profileData.learned_information.length > 0) {
      profileData.learned_information.forEach(fact => {
        if (fact && fact.fact && fact.fact.trim()) {
          allPersonalFacts.push(fact.fact);
        }
      });
    } else if (!Array.isArray(profileData.learned_information)) {
      logger.error('learned_information is not an array', { learned_information: profileData.learned_information });
    }
    
    // Add all personal facts if any exist
    if (allPersonalFacts.length > 0) {
      // Add personal info header
      dynamicPrompt += `\nHere is some other information about the user: `;
      
      // Add all personal facts
      allPersonalFacts.forEach(fact => {
        dynamicPrompt += `\n${fact}`;
      });
      
      // Add usage instructions
      dynamicPrompt += `\nUse this personal information about the user to tailor your responses appropriately. 
For example, if the user indicates he (or she) is a doctor, present the information in a manner suitable for a doctor as opposed to a regular person; that is, don't explain remedial information to a doctor as you would a regular person, or like a doctor speaking to a patient.  
Similarly, if the user is a pilot, assume that he knows the basics of airplanes, piloting, and flying when you respond to him on a topic concerning these things.  
If the user indicates he has a hobby or interest, such as "I am an avid tennis player", then engage with him when the conversation topic goes there, as you can assume he is interested.
Using this personal information effectively means that you know more about your user, and you can be more helpful and interesting to him, and of better service which is your primary purpose and motivation.`;
    }
    
    // Add subject-specific prompt if available
    try {
      const subjectPromptText = getSubjectPromptText();
      if (subjectPromptText && subjectPromptText.trim()) {
        dynamicPrompt += `\n\n${subjectPromptText}`;
        logger.debug('Subject-specific prompt appended', { subjectPromptLength: subjectPromptText.length });
      }
    } catch (error) {
      logger.error('Failed to get subject prompt text', { error: error.message });
    }
    
    // Create dynamic system prompt object
    const dynamicSystemPrompt = {
      subject: "system",
      prompt: dynamicPrompt,
      category: null,
      version: null,
      last_updated: new Date().toISOString(),
      metadata: null
    };
    
    // Save to sessionStorage
    setDynamicSystemPrompt(dynamicSystemPrompt);
    
    logger.debug('Dynamic system prompt built and saved successfully');
    
  } catch (error) {
    logger.error('Failed to build dynamic system prompt', { error: error.message });
  }
};
